import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { EmployeeService, IMesUser } from '@services/mesuser.service';
import { SessionStorageService } from '@services/storage.service';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';
import { catchError, finalize, forkJoin, fromEvent, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

interface selectedTask {
	employee: IMesUser;
	task: IWorktask;
	checkinTime: Date;
}

@Component({
	selector: 'app-mes-cjs-terminal',
	templateUrl: './mes-cjs-terminal.component.html',
	styleUrls: ['./mes-cjs-terminal.component.scss'],
	animations: [
		trigger('inOutAnimation', [transition(':enter', [style({ transform: 'scale(.99)', opacity: 0.5 }), animate('.4s ease-out', style({ transform: 'scale(1)', opacity: 1 }))]), transition(':leave', [style({ transform: 'scale(1)' }), animate('.4s ease-out', style({ transform: 'scale(.99)', opacity: 0.5 }))])]),
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class MesCjsTerminalComponent implements OnInit, AfterViewInit {
	public loadingEmployee = false;
	public employeeNumber: string = '';
	public employeeData$: Observable<any>;
	public workTasks$: Observable<IWorktask[]>;
	public offWorkTask$: Observable<IWorktask[]>;

	public currentErrorCode = '';
	public currentErrorDetails;

	public errorCodes = {
		EMPLOYEE_DOES_NOT_EXIST: {
			text: 'Det indtastede er ikke et gyldigt lønnummer',
			cssClass: 'errormsg',
		},
		EMPLOYEE_NUMBER_EMPTY: {
			text: 'Indtast venligst et lønnummer',
			cssClass: 'neutralmsg',
		},
		SERVER_ERROR: {
			text: 'Serverfejl. Se detaljer nedenfor. Kontakt systemets administrator.',
			cssClass: 'errormsg',
		},
		EMPLOYEE_HAS_NO_ACTIVE_TASK: {
			text: 'Medarbejderen har ikke nogen aktiv arbejdsopgave',
			cssClass: 'neutralmsg',
		},
		EMPLOYEE_IS_INACTIVE: {
			text: 'Medarbejderen er inaktiv',
			cssClass: 'neutralmsg',
		},
	};

	@ViewChild('employeeNumberInputField')
	employeeNumberInputField: ElementRef<HTMLInputElement>;

	@ViewChild('submitButton') submitButton: ElementRef<HTMLInputElement>;
	@ViewChild('form') form: ElementRef<HTMLFormElement>;
	@ViewChild('selectWorktaskForm')
	selectWorktaskForm: ElementRef<HTMLFormElement>;

	private emplNumberVal: Subject<string> = new Subject<string>();
	private employeeEntered = false;
	public emplNumberVal$: Observable<string> = this.emplNumberVal.asObservable();

	public showLoadingOverlay = false;
	public keyBuffer = '';
	private workTasks: IWorktask[];
	private employeeData: IMesUser;

	private generalPrefix = 'ACS_';
	private worktaskPrefix = 'W';
	private employeePrefix = 'E';
	private generalSuffix = ''; // Kunne bruges til at terminere streng fra scanner, fx "#ACSEND" ==> #ACS_W_12345#ACSEND
	public userSelectedTask$: Observable<selectedTask>;

	constructor(
		private mesEmployeeService: EmployeeService,
		private mesWorkTaskService: WorktaskService,
		private sessionStorageService: SessionStorageService,
		public dialog: MatDialog,
	) {}

	validateKey(key: KeyboardEvent) {
		// let illegal = ['Enter', 'Shift', 'Backspace', 'Arrow', 'Alt']
		// return !key.code !== 'Enter' && key.code.indexOf('Arrow') === -1 && key.code.indexOf('Shift') === -1
		return key.key.length === 1;
	}

	parseQrCommand(command: string) {
		const commandPos = command.indexOf(this.generalPrefix);
		if (commandPos === -1) return;
		const rest = command.substring(commandPos).replace(this.generalPrefix, '');
		const restParts = rest.split('_');
		switch (restParts[0]) {
			case this.employeePrefix:
				if (!this.employeeEntered) {
					this.employeeNumberInputField.nativeElement.value = '';
					this.emplNumberVal.next(restParts[1]);
				}
				break;

			case this.worktaskPrefix:
				if (this.employeeEntered) {
					const wc = this.workTasks.filter((wc) => wc.alias === restParts[1]);
					this.taskClicked(wc[0], this.employeeData);
				} else {
					this.employeeNumberInputField.nativeElement.value = '';
				}
				break;
		}
		this.keyBuffer = '';
	}

	ngOnInit() {
		fromEvent<KeyboardEvent>(document, 'keydown').subscribe((e) => {
			if (this.validateKey(e)) {
				this.keyBuffer += e.key;
			} else if (e.code === 'Enter') {
				this.parseQrCommand(this.keyBuffer);
			}
		});

		this.userSelectedTask$ = this.sessionStorageService.watch('MES.CheckedInEmployee').pipe(
			tap((val) => {
				if (val.trim()) {
					setTimeout(() => {
						this.sessionStorageService.set('MES.CheckedInEmployee', '');
					}, 3000);
				}
			}),
			map((val) => (val ? JSON.parse(val.trim()) : '')),
		);
	}

	ngAfterViewInit() {
		this.setupFormObservable();
		this.keepFocused();

		this.workTasks$ = this.mesWorkTaskService.getWorktasks(/* der kunne være et terminalId her */).pipe(
			tap(() => {
				this.keyBuffer = '';
			}),
			map((worktasks) => {
				this.workTasks = worktasks.result;
				return worktasks.result.filter((worktask) => worktask.isActive).sort((a, b) => (a.name.toLowerCase() >= b.name.toLowerCase() ? 1 : -1));
			}),
		);

		this.offWorkTask$ = this.mesWorkTaskService.getWorktasks(/* der kunne være et terminalId her */).pipe(
			tap(() => {
				this.keyBuffer = '';
			}),
			map((worktasks) => {
				return worktasks.result.filter((worktask) => worktask.alias === 'Off');
			}),
		);

		this.employeeData$ = this.emplNumberVal$.pipe(
			// debounceTime(50), // Bør ikke være nødvendig, når vi også har distinctUntilChanged
			// filter((e: FormDataEvent) => e instanceof SubmitEvent), // Lyt kun på submit-events fra formularen
			// switchMap(() => of(this.employeeNumberInputField.nativeElement.value.trim())), // Returner en ny observable, som emitter feltets værdi
			distinctUntilChanged((prev, curr) => {
				// Lav ikke nyt API-request, hvis feltværdien er uændret
				if (!prev && !curr) return false; // For at sikre, at css in_out-animationen vises, hver gang man trykker Enter i et helt tomt felt
				return prev === curr;
			}),
			tap(() => {
				// "Side-effects" håndteres i tap()
				this.loadingEmployee = true;
				this.currentErrorCode = '';
			}),
			// map(enteredEmployeeNumber => {
			//   return enteredEmployeeNumber.replace(/[^a-zA-Z0-9 ]/g, '').trim();
			// }),
			// tap(n => {
			//   this.employeeNumberInputField.nativeElement.value = n;
			// }),
			switchMap((enteredEmployeeNumber: string) => {
				if (!enteredEmployeeNumber) {
					return this.handleError('EMPLOYEE_NUMBER_EMPTY');
				}
				return this.mesEmployeeService.getEmployeeBySalaryKey(enteredEmployeeNumber).pipe(
					catchError((err) => {
						if (err.status === 404) {
							return this.handleError('EMPLOYEE_DOES_NOT_EXIST');
						} else {
							return this.handleError('SERVER_ERROR', err);
						}
					}),

					// Værdien af "employee" vil være null pga. "of(null)" fra handleError(), hvis getEmployeeBySalaryKey giver en 404.
					// Vi er nødt til at håndtere dette, så employeeData$ ikke ender med at emitte værdien fra getWorktask i denne switchMap().
					// Send derfor en tom observable ud, så der ikke sættes data i employeeData$ og viewet dermed skifter til "vælg task".
					switchMap((employee) => {
						if (employee === null) return of(undefined);
						this.employeeEntered = true;
						this.employeeData = employee.result;

						if (!employee.result.isActive) {
							return this.handleError('EMPLOYEE_IS_INACTIVE');
						}

						const obsCurrentTask = this.mesEmployeeService.getActiveWorkTask(employee.result.id).pipe(
							catchError((err) => {
								if (err.status === 404) {
									return this.handleError('EMPLOYEE_HAS_NO_ACTIVE_TASK');
								} else {
									return this.handleError('SERVER_ERROR', err);
								}
							}),
							map((worktask) => (worktask ? worktask.result : null)),
						);

						const obsFavTasks = this.mesEmployeeService.getFavouriteWorkTasks(employee.result.id, 6, 100).pipe(
							catchError((err) => {
								return this.handleError('SERVER_ERROR', err);
							}),
							map((favouriteWorkTasks) => {
								return favouriteWorkTasks.result;
							}),
						);

						return forkJoin([of(employee.result), obsCurrentTask, obsFavTasks]);
					}),
				);
			}),
			finalize(() => {
				this.loadingEmployee = false;
				this.keepFocused();
			}),
		);
	}

	handleError(errorType, errorDetails: any = false) {
		this.currentErrorDetails = errorDetails;
		// For at sikre, at css-klassen når at blive fjernet og derpå tilføjet igen, så animationen vises hver gang
		setTimeout(() => (this.currentErrorCode = errorType), 0);
		this.loadingEmployee = false; // Skjul spinner
		setTimeout(() => this.keepFocused());
		return of(null);
	}

	resetEmployeeNumber() {
		this.employeeEntered = false;
		this.emplNumberVal.next('');
		setTimeout(() => {
			this.setupFormObservable();
			this.currentErrorCode = '';
		}, 0);
		// setTimeout(() => this.emplNumberVal.next("1001"), 1000); // Skift lønnummer programmatisk
	}

	keepFocused(_: any = false) {
		this.employeeNumberInputField.nativeElement.focus();
	}

	setupFormObservable() {
		fromEvent<FormDataEvent>(this.form.nativeElement, 'submit')
			.pipe(
				switchMap((_) => {
					const val = this.employeeNumberInputField.nativeElement.value.replace(/[^a-z0-9 ]/gi, '').trim();
					this.employeeNumber = this.employeeNumberInputField.nativeElement.value = val;
					this.emplNumberVal.next(this.employeeNumber);
					return of(null);
				}),
			)
			.subscribe();
	}

	toggleLoadingOverlay() {
		this.showLoadingOverlay = !this.showLoadingOverlay;
	}

	storeSelectedTask(task: IWorktask, employee: IMesUser) {
		const json: selectedTask = { task, employee, checkinTime: new Date() };
		this.sessionStorageService.set('MES.CheckedInEmployee', JSON.stringify(json));
	}

	taskClicked(task: IWorktask, employee: IMesUser) {
		this.toggleLoadingOverlay();
		this.mesEmployeeService
			.checkIn(employee.id, task.id)
			.pipe(
				tap(() => {
					this.toggleLoadingOverlay();
				}),
				catchError((err) => {
					this.toggleLoadingOverlay();
					return this.handleError('SERVER_ERROR', err);
				}),
				// catchError((err) => {}),
			) // Error handling missing here
			.subscribe((checkInData) => {
				console.log(checkInData);
				this.storeSelectedTask(task, employee);
				this.resetEmployeeNumber();

				/*
        this.dialog
          .open(DialogMesTaskSelected, {
            width: '1000px',
            height: '550px',
            data: {
              task: task,
              employee: employee,
            },
          })
          .afterClosed()
          .subscribe(res => {
            if (res === true) {
              this.resetEmployeeNumber();
            }
          });
          */
			});
	}
}

@Component({
	imports: [CommonModule, RouterModule],
	selector: 'app-dialog-mes-task-selected',
	templateUrl: 'dialog-mes-task-selected.html',
	styleUrls: ['dialog-mes-task-selected.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true,
})
export class DialogMesTaskSelected implements OnInit {
	public task: IWorktask;
	public employee: IMesUser;
	public timer: number = 5;
	private timerInterval;

	constructor(
		public dialogRef: MatDialogRef<DialogMesTaskSelected>,
		@Inject(MAT_DIALOG_DATA) public data: any,
	) {}

	ngOnInit() {
		this.task = this.data.task;
		this.employee = this.data.employee;
		this.timerInterval = setInterval(() => {
			this.timer--;
			if (this.timer === 0) {
				clearInterval(this.timerInterval);
				this.dialogRef.close(true);
			}
		}, 1000);
	}

	changeTask(): void {
		this.dialogRef.close();
	}

	confirmSelection() {
		this.dialogRef.close(true);
	}
}
